import { t } from '../../company/labels';
class ActivityLogsDelegate {
    // Action type translations
    getActionLabel(action) {
        const labels = {
            CREATE: 'Crear',
            UPDATE: 'Actualizar',
            LOGIN: 'Iniciar sesión',
            LOGOUT: 'Cerrar sesión',
            VIEW: 'Ver',
            EXPORT: 'Exportar',
            IMPORT: 'Importar',
            BULK_UPDATE: 'Actualización masiva',
        };
        return labels[action] || action;
    }

    // Action type colors
    getActionColor(action) {
        const colors = {
            CREATE: '#10b981', // green
            UPDATE: '#3b82f6', // blue
            LOGIN: '#8b5cf6', // purple
            LOGOUT: '#6b7280', // gray
            VIEW: '#94a3b8', // slate
            EXPORT: '#f59e0b', // amber
            IMPORT: '#06b6d4', // cyan
            BULK_UPDATE: '#f97316', // orange
        };
        return colors[action] || '#6b7280';
    }

    // Action type → design-system tone (StatusTag)
    // Derived from getActionColor so the action palette has a single source.
    getActionTone(action) {
        const tones = {
            '#10b981': 'success',
            '#3b82f6': 'brand',
            '#8b5cf6': 'violet',
            '#6b7280': 'neutral',
            '#94a3b8': 'neutral',
            '#f59e0b': 'warning',
            '#06b6d4': 'info',
            '#f97316': 'warning',
        };
        return tones[this.getActionColor(action)] || 'neutral';
    }

    // User type → design-system tone (StatusTag)
    getUserTypeTone(userType) {
        const tones = {
            admin: 'violet',
            gerente: 'brand',
            asesor: 'info',
            promotor: 'success',
            auditor: 'warning',
            location: 'neutral',
        };
        return tones[userType] || 'neutral';
    }

    // Entity type translations
    getEntityLabel(entityType) {
        const labels = {
            CLIENT: 'Cliente',
            STORE: 'Tienda',
            USER: 'Usuario',
            PRODUCT: 'Producto',
            PRICE: 'Precio',
            STORETYPE: 'Tipo de tienda',
            POLL: 'Encuesta',
            NOTIFICATION: 'Notificación',
            CAMPAIGN: 'Campaña',
            CONTRACT: 'Contrato',
            TAG: 'Etiqueta',
            CALENDAR: 'Calendario',
            COMPETITION: 'Competencia',
            STORE_CHECK: 'Store Check',
            AUDIT: 'Auditoría',
            PERMISSION: 'Permisos',
            SYSTEM: 'Sistema',
        };
        return labels[entityType] || entityType;
    }

    // Entity type icons (Ant Design icon names)
    getEntityIcon(entityType) {
        const icons = {
            CLIENT: 'TeamOutlined',
            STORE: 'ShopOutlined',
            USER: 'UserOutlined',
            PRODUCT: 'AppstoreOutlined',
            PRICE: 'DollarOutlined',
            STORETYPE: 'TagsOutlined',
            POLL: 'FormOutlined',
            NOTIFICATION: 'BellOutlined',
            CAMPAIGN: 'FlagOutlined',
            CONTRACT: 'FileTextOutlined',
            TAG: 'TagOutlined',
            CALENDAR: 'CalendarOutlined',
            COMPETITION: 'TrophyOutlined',
            STORE_CHECK: 'CheckSquareOutlined',
            AUDIT: 'AuditOutlined',
            SYSTEM: 'SettingOutlined',
        };
        return icons[entityType] || 'FileOutlined';
    }

    // User type labels
    getUserTypeLabel(userType) {
        const labels = {
            admin: 'Administrador',
            gerente: 'Gerente',
            asesor: 'Asesor',
            promotor: t('promotor'),
            auditor: 'Auditor',
            location: 'Ubicación',
            TradeMarketing: 'Trade Marketing',
            auditAnalyst: 'Análisis Auditor',
        };
        return labels[userType] || userType;
    }

    // Format date for display
    formatDate(dateString) {
        if (!dateString) return '-';
        const date = new Date(dateString);
        return date.toLocaleString('es-VE', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        });
    }

    // Format relative time
    formatRelativeTime(dateString) {
        if (!dateString) return '-';
        const date = new Date(dateString);
        const now = new Date();
        const diffMs = now - date;
        const diffMins = Math.floor(diffMs / 60000);
        const diffHours = Math.floor(diffMs / 3600000);
        const diffDays = Math.floor(diffMs / 86400000);

        if (diffMins < 1) return 'Hace un momento';
        if (diffMins < 60) return `Hace ${diffMins} min`;
        if (diffHours < 24) return `Hace ${diffHours} h`;
        if (diffDays < 7) return `Hace ${diffDays} días`;
        return this.formatDate(dateString);
    }

    // Check if update includes deactivation
    isDeactivation(log) {
        if (log.action !== 'UPDATE') return false;
        return log.changedFields?.includes('active') && log.newData?.active === false;
    }

    // Check if update includes activation
    isActivation(log) {
        if (log.action !== 'UPDATE') return false;
        return log.changedFields?.includes('active') && log.newData?.active === true;
    }

    // Get description with context
    getLogDescription(log) {
        if (log.description) return log.description;
        
        const entityLabel = this.getEntityLabel(log.entityType).toLowerCase();
        const entityName = log.entityName || log.entityId;
        
        switch (log.action) {
            case 'CREATE':
                return `Creó ${entityLabel} "${entityName}"`;
            case 'UPDATE':
                if (this.isDeactivation(log)) {
                    return `Desactivó ${entityLabel} "${entityName}"`;
                }
                if (this.isActivation(log)) {
                    return `Activó ${entityLabel} "${entityName}"`;
                }
                return `Actualizó ${entityLabel} "${entityName}"`;
            case 'LOGIN':
                return 'Inició sesión';
            case 'LOGOUT':
                return 'Cerró sesión';
            default:
                return `${this.getActionLabel(log.action)} ${entityLabel}`;
        }
    }
}

export default ActivityLogsDelegate;
